import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Bookmark as BookmarkIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bookmarks = () => {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks/list`, {
        withCredentials: true
      });
      setBookmarks(response.data.bookmarks);
    } catch (error) {
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBookmark = async (chemicalId) => {
    try {
      await axios.delete(`${API}/bookmarks/remove/${chemicalId}`, {
        withCredentials: true
      });
      setBookmarks(bookmarks.filter(b => b.chemical_id !== chemicalId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  const getStatusClass = (color) => {
    if (color === 'red') return 'bg-red-500';
    if (color === 'yellow') return 'bg-yellow-500';
    if (color === 'green') return 'bg-green-500';
    return 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    if (status === 'not_compatible') return 'Not Compatible';
    if (status === 'compatible') return 'Compatible';
    if (status === 'caution') return 'Caution';
    return status;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Bookmarked Chemicals
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        ) : bookmarks.length === 0 ? (
          <Card className="glass border-white/20">
            <CardContent className="py-12 text-center">
              <BookmarkIcon className="w-16 h-16 mx-auto text-white/50 mb-4" />
              <p className="text-white text-lg">No bookmarks yet</p>
              <p className="text-white/70 text-sm mt-2">Bookmark important chemicals for quick access</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="bookmarks-list">
            {bookmarks.map((bookmark) => (
              <Card key={bookmark.id} className="glass border-white/20 card-hover">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-white text-xl">{bookmark.chemical_name}</CardTitle>
                      <p className="text-white/70 text-sm mt-1">
                        Bookmarked on {new Date(bookmark.bookmarked_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`${getStatusClass(bookmark.compatibility_color)} px-4 py-2 rounded-full text-white text-sm font-semibold`}>
                        {getStatusLabel(bookmark.compatibility_status)}
                      </span>
                      <Button
                        data-testid={`remove-bookmark-${bookmark.chemical_id}`}
                        onClick={() => handleRemoveBookmark(bookmark.chemical_id)}
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Bookmarks;
